import { compareVersionStrings } from '../../src/software-lifecycle/purity-upgrades/purity-upgrades.utils';

const MIN_PURITY_VERSION_6_5 = '6.5.11';
const MIN_PURITY_VERSION_6_7 = '6.7.4';
const MIN_PURITY_VERSION_DEFAULT = '6.8.5';

/**
 * Checks if the given Purity version is supported for automated capacity down licensing (CDL).
 *
 * Version support rules:
 * - Versions below 6.5.0 are not supported.
 * - For 6.5.x, only 6.5.11 or higher is supported.
 * - For 6.6.x and 6.7.x, only 6.7.4 or higher is supported.
 * - For 6.8.0 and above, only 6.8.5 or higher is supported.
 *
 * NOTE: compareVersionStrings returns a negative number if version2 < version1,
 * a positive number if version2 > version1, or 0 if equal.
 *
 * @param version The Purity version string to check (e.g., '6.7.4').
 * @returns True if the version is supported for CDL, false otherwise.
 */
export function isArrayVersionSupported(version: string): boolean {
    // If version < 6.5.0, not supported
    if (compareVersionStrings(version, '6.5.0') > 0) {
        return false;
    }
    // If version in [6.5.0, 6.6.0), must be >= 6.5.11
    if (compareVersionStrings(version, '6.6.0') > 0) {
        return compareVersionStrings(version, MIN_PURITY_VERSION_6_5) <= 0;
    }
    // If version in [6.6.0, 6.8.0), must be >= 6.7.4
    if (compareVersionStrings(version, '6.8.0') > 0) {
        return compareVersionStrings(version, MIN_PURITY_VERSION_6_7) <= 0;
    }
    // If version >= 6.8.0, must be >= 6.8.5
    return compareVersionStrings(version, MIN_PURITY_VERSION_DEFAULT) <= 0;
}

/**
 * Returns the minimum supported Purity version for the given current version line.
 * Used to recommend the correct upgrade target for automated CDL support.
 *
 * @param currentVersion The current Purity version string (e.g., '6.6.2').
 * @returns The minimum required Purity version string for CDL support.
 */
export function getSupportedPurityVersion(currentVersion: string): string {
    if (compareVersionStrings(currentVersion, '6.5.0') > 0 || currentVersion.startsWith('6.5')) {
        return MIN_PURITY_VERSION_6_5;
    } else if (compareVersionStrings(currentVersion, '6.8.0') > 0) {
        return MIN_PURITY_VERSION_6_7;
    }
    return MIN_PURITY_VERSION_DEFAULT;
}

/**
 * Returns true if either the agent status is unknown or the agent is installed and running the recommended version.
 * This indicates that the agent is streamlined for automated capacity down licensing.
 */
export function isAgentStreamlined(
    agentInstalled: boolean,
    agentRunningVersion: string,
    agentRecommendedVersion: string,
): boolean {
    return (
        agentStatusUnknown(agentInstalled, agentRunningVersion, agentRecommendedVersion) ||
        (agentInstalled && agentRunningVersion === agentRecommendedVersion)
    );
}

export function agentStatusUnknown(
    agentInstalled: boolean,
    agentRunningVersion: string,
    agentRecommendedVersion: string,
): boolean {
    return agentInstalled == null || (agentInstalled && (!agentRunningVersion || !agentRecommendedVersion));
}
